import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

const STATUS_MAP = {
  orcamento:              { label: 'Orçamento',        cor: '#6c757d' },
  em_andamento:           { label: 'Em Andamento',     cor: '#1a4e7a' },
  em_andamento_alerta:    { label: 'Em Andamento',     cor: '#e67e22' },
  atrasada:               { label: 'Atrasada',         cor: '#dc3545' },
  concluida:              { label: 'Entregue',         cor: '#28a745' },
  concluida_pgto_pendente:{ label: 'Pgto. Pendente',   cor: '#d68910' },
  finalizada:             { label: 'Finalizada',       cor: '#28a745' },
  cancelada:              { label: 'Cancelada',        cor: '#6c757d' },
  pausada:                { label: 'Pausada',          cor: '#6c757d' },
  orcamento_recusado:     { label: 'Orç. Recusado',    cor: '#dc3545' },
};

const IcoBusca = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
  </svg>
);

export default function BuscaGlobal() {
  const navigate = useNavigate();
  const [aberta, setAberta] = useState(false);
  const [query, setQuery] = useState('');
  const [resultados, setResultados] = useState({ obras: [], clientes: [] });
  const [carregando, setCarregando] = useState(false);
  const [indiceSelecionado, setIndiceSelecionado] = useState(-1);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    function onKeydown(e) {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        setAberta(true);
      }
    }
    function onAbrir() { setAberta(true); }
    window.addEventListener('keydown', onKeydown);
    window.addEventListener('busca-abrir', onAbrir);
    return () => {
      window.removeEventListener('keydown', onKeydown);
      window.removeEventListener('busca-abrir', onAbrir);
    };
  }, []);

  useEffect(() => {
    if (aberta) {
      setQuery('');
      setResultados({ obras: [], clientes: [] });
      setIndiceSelecionado(-1);
      setTimeout(() => inputRef.current?.focus(), 40);
    }
  }, [aberta]);

  const buscar = useCallback((q) => {
    clearTimeout(debounceRef.current);
    if (!q || q.trim().length < 2) {
      setResultados({ obras: [], clientes: [] });
      setCarregando(false);
      return;
    }
    setCarregando(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await api.get(`/busca?q=${encodeURIComponent(q.trim())}`);
        setResultados(res.data);
        setIndiceSelecionado(-1);
      } catch {
        // silently fail
      } finally {
        setCarregando(false);
      }
    }, 300);
  }, []);

  function handleChange(e) {
    const v = e.target.value;
    setQuery(v);
    buscar(v);
  }

  const totalObras = resultados.obras.length;

  function getIndice(tipo, i) {
    return tipo === 'obra' ? i : totalObras + i;
  }

  function navegar(tipo, item) {
    setAberta(false);
    if (tipo === 'obra') navigate(`/obras/${item.obraid}`);
    else navigate('/clientes');
  }

  function handleKeyDown(e) {
    const total = totalObras + resultados.clientes.length;
    if (e.key === 'Escape') { setAberta(false); return; }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setIndiceSelecionado(i => Math.min(i + 1, total - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setIndiceSelecionado(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && indiceSelecionado >= 0) {
      if (indiceSelecionado < totalObras) {
        navegar('obra', resultados.obras[indiceSelecionado]);
      } else {
        navegar('cliente', resultados.clientes[indiceSelecionado - totalObras]);
      }
    }
  }

  const temResultados = totalObras > 0 || resultados.clientes.length > 0;

  return (
    <>
      {aberta && (
        <div className="busca-overlay" onClick={() => setAberta(false)}>
          <div className="busca-modal" onClick={e => e.stopPropagation()}>
            <div className="busca-input-wrap">
              <IcoBusca />
              <input
                ref={inputRef}
                className="busca-input"
                placeholder="Buscar obras, clientes..."
                value={query}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                autoComplete="off"
              />
              {carregando
                ? <div className="busca-spinner" />
                : <kbd className="busca-esc-hint" onClick={() => setAberta(false)}>Esc</kbd>
              }
            </div>

            {temResultados && (
              <div className="busca-resultados">
                {resultados.obras.length > 0 && (
                  <div>
                    <div className="busca-grupo-titulo">Obras</div>
                    {resultados.obras.map((o, i) => {
                      const idx = getIndice('obra', i);
                      const st = STATUS_MAP[o.status_obra] || { label: o.status_obra, cor: '#999' };
                      return (
                        <div
                          key={o.obraid}
                          className={`busca-item${indiceSelecionado === idx ? ' busca-item-ativo' : ''}`}
                          onMouseEnter={() => setIndiceSelecionado(idx)}
                          onClick={() => navegar('obra', o)}
                        >
                          <span className="busca-item-icone">🏗️</span>
                          <div className="busca-item-texto">
                            <div className="busca-item-nome">{o.nome_obra}</div>
                            <div className="busca-item-sub">{o.nome_cliente}</div>
                          </div>
                          <span
                            className="busca-item-badge"
                            style={{ background: st.cor + '22', color: st.cor }}
                          >
                            {st.label}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}

                {resultados.clientes.length > 0 && (
                  <div>
                    <div className="busca-grupo-titulo">Clientes</div>
                    {resultados.clientes.map((c, i) => {
                      const idx = getIndice('cliente', i);
                      return (
                        <div
                          key={c.clienteid}
                          className={`busca-item${indiceSelecionado === idx ? ' busca-item-ativo' : ''}`}
                          onMouseEnter={() => setIndiceSelecionado(idx)}
                          onClick={() => navegar('cliente', c)}
                        >
                          <span className="busca-item-icone">👤</span>
                          <div className="busca-item-texto">
                            <div className="busca-item-nome">{c.nome_cliente}</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {!temResultados && query.trim().length >= 2 && !carregando && (
              <div className="busca-vazio">Nenhum resultado para "{query}"</div>
            )}

            {query.trim().length < 2 && (
              <div className="busca-dica">
                Use as setas ↑↓ para navegar · Enter para abrir · Esc para fechar
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
